/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { GetServerSideProps } from 'next';
import moment from 'moment';

import { PageRendererComponent, prePageCall } from '@marriott/mi-headless-utils';
import {
  NEXT_PUBLIC_ENV_KEYS,
  OFFERS_APP_DOMAIN,
  getDeepLinkParams,
  processAcceptLang,
} from '@marriott/mi-offers-components';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import OfferSignatures from '../../signatures/offerSignatures.json';
const { SESSION_APP_DOMAIN_URL } = process.env;

const pageRenderer = function (props: any) {
  console.log('[OSERP] findOffers page props ', props);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  updateGlobalData('sessionData', props?.sessionData);
  return <PageRendererComponent {...props} />;
};

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const offersSpecificData = async (context: any, parallelCallsResponse: any) => {
  const { req } = context;
  const { log } = global.loggerInstance('offersSpecificData');
  const requestId = req?.headers['x-request-id'];
  const sessionID = req?.cookies?.sessionID ?? '';
  const currentLocale = req?.headers['accept-language'];
  const { sessionData } = parallelCallsResponse;
  const acceptLang = processAcceptLang(currentLocale ?? '')?.toLowerCase();
  const searchQuery = sessionData?.cacheData?.data?.AriesCommon?.page_url_query_string;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const urlParams = new URLSearchParams(searchQuery);
  const isFlexibleDateSearch = urlParams?.has('flexibleDateSearch') && urlParams?.get('flexibleDateSearch') == 'true';
  const deepLinkParams = getDeepLinkParams(urlParams);
  const {
    searchByOffersIds,
    searchByPropertyCodes,
    searchByStayBasedOfferFlag,
    searchByGeoLoc,
    searchBystate,
    searchBycountry,
    destinationSearched,
  } = deepLinkParams;
  const skipDestinationCall =
    searchByOffersIds ||
    searchByPropertyCodes ||
    searchByStayBasedOfferFlag ||
    searchByGeoLoc ||
    searchBystate ||
    searchBycountry;

  const [featureFlagLogo, featureFlagAemltsHost] = await Promise.all([
    // Feature flag call for logo automation
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
      target: TARGETS.APP.OFFERS_RENDERER,
    }),
    // Feature flag to switch AEM host to LTS host
    fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_OFFERS_RENDERER_AEMLTS_HOST, {
      target: TARGETS.APP.OFFERS_RENDERER,
      enableStream: true,
    }),
  ]);
  const useAemLtsHost = featureFlagAemltsHost?.experience_mcom_offers_renderer_aemlts_host;
  const aemHost = useAemLtsHost ? process.env.NEXT_PUBLIC_AEM_LTS_HOST : process.env.NEXT_PUBLIC_AEM_HOST;

  let updatedSessionData: any = sessionData;
  // update sessionData utility server side
  const updateSessionObjfunc = async (ariesSearch: any = {}) => {
    try {
      const url = `${SESSION_APP_DOMAIN_URL}/phoenix-gateway/session`;
      const headers = {
        Cookie: `sessionID=${sessionID}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
      };
      const payload = {
        createOrUpdate: {
          AriesSearch: ariesSearch,
        },
        remove: [],
        sessionToken: sessionID,
      };
      await fetch(url, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(payload),
      })
        .then(res => res.json())
        .then(data => {
          return data?.status;
        });
    } catch (error) {
      log.debug(`[OSERP] update session API Failed:, ${error}`);
    }
  };
  //fetch suggested destination details service
  const getDestinationDetails = async () => {
    let url = `${OFFERS_APP_DOMAIN}`;
    const query = `query=${destinationSearched}`;
    try {
      url = url.replace('/offers/details/', `/offers/findOffers/getDestinationDetails?${query}`);
      const headers = {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId ?? '',
        'Accept-Language': acceptLang ?? 'en-US',
        sessionToken: sessionID ?? '',
      };
      const body = {
        sessionToken: sessionID ?? '',
      };
      await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
      })
        .then(res => res.json())
        .then(data => {
          log.debug(`[OSERP] Suggested Details data from API: ${JSON.stringify(data)}`);
          updateSessionData(data);
        });
    } catch (error) {
      log.debug(`[OSERP] getDestinationDetails API Failed:, ${error}`);
    }
  };
  const updateSessionData = async (suggestedPlaceDetails: any) => {
    log.debug(`[OSERP] Suggested Details data: ${JSON.stringify(suggestedPlaceDetails)}`);
    let ariesSearch: any = sessionData?.cacheData?.data?.AriesSearch;
    ariesSearch = {
      ...ariesSearch,
      searchCriteria: {
        ...ariesSearch?.searchCriteria,
        address: {
          city:
            suggestedPlaceDetails?.city ||
            (urlParams?.has('destinationAddress.city') ? urlParams?.get('destinationAddress.city') : ''),
          country:
            suggestedPlaceDetails?.country ||
            (urlParams?.has('destinationAddress.country') ? urlParams?.get('destinationAddress.country') : ''),
          destination:
            suggestedPlaceDetails?.destination ||
            (urlParams?.has('destinationAddress.destination') ? urlParams?.get('destinationAddress.destination') : ''),
          latitude:
            parseFloat(suggestedPlaceDetails?.latitude) ||
            (urlParams?.has('destinationAddress.latitude')
              ? parseFloat(urlParams?.get('destinationAddress.latitude') ?? '')
              : ''),
          longitude:
            parseFloat(suggestedPlaceDetails?.longitude) ||
            (urlParams?.has('destinationAddress.longitude')
              ? parseFloat(urlParams?.get('destinationAddress.longitude') ?? '')
              : ''),
          stateProvince:
            suggestedPlaceDetails?.stateProvince ||
            (urlParams?.has('destinationAddress.stateProvince')
              ? urlParams?.get('destinationAddress.stateProvince')
              : ''),
          stateProvinceDisplayName: urlParams?.has('destinationAddress.stateProvinceDisplayName')
            ? urlParams?.get('destinationAddress.stateProvinceDisplayName')
            : '',
          destinationType: suggestedPlaceDetails?.destinationType,
        },
        countryName: urlParams?.has('countryName') ? urlParams?.get('countryName') : '',
        destinationAddressMainText:
          suggestedPlaceDetails?.mainText ||
          (urlParams?.has('destinationAddress.city') ? urlParams?.get('destinationAddress.city') : ''),
        destinationAddressPlaceId:
          suggestedPlaceDetails?.placeId ||
          (urlParams?.has('destinationAddress.placeId') ? urlParams?.get('destinationAddress.placeId') : ''),
        destinationAddressSecondaryText:
          suggestedPlaceDetails?.secondaryText ||
          (urlParams?.has('destinationAddress.secondaryText')
            ? urlParams?.get('destinationAddress.secondaryText')
            : ''),
        geoCode:
          suggestedPlaceDetails?.geoCode ||
          (urlParams?.has('destinationAddress.latitude')
            ? `${urlParams?.get('destinationAddress.latitude')}, ${urlParams?.get('destinationAddress.longitude')}`
            : ''),
        availabilityRequestVO: {
          ...ariesSearch?.searchCriteria?.availabilityRequestVO,
          checkInDate: urlParams?.has('fromDate')
            ? moment(urlParams?.get('fromDate'), currentLocale === 'en-GB' ? 'DD/MM/YYYY' :  currentLocale === 'ar-AE' ? 'YYYY/MM/DD' : 'MM/DD/YYYY').toISOString()
            : null,
          checkOutDate: urlParams?.has('toDate')
            ? moment(urlParams?.get('toDate'), currentLocale === 'en-GB' ? 'DD/MM/YYYY' :  currentLocale === 'ar-AE' ? 'YYYY/MM/DD' : 'MM/DD/YYYY').toISOString()
            : null,
          flexibleDate: isFlexibleDateSearch,
          lengthOfStay: urlParams?.has('lengthOfStay') ? urlParams?.get('lengthOfStay') : null,
          flexibleDateSearchRateDisplay: false,
          hideFlexibleDateCalendar: false,
          numAdultsPerRoom: deepLinkParams?.numAdultsPerRoom ? deepLinkParams?.numAdultsPerRoom : 1,
          numChildrenPerRoom: deepLinkParams?.numChildrenPerRoom ? deepLinkParams?.numChildrenPerRoom : 0,
          numGuestsPerRoom: deepLinkParams?.numGuestsPerRoom ? deepLinkParams?.numGuestsPerRoom : 1,
          numRooms: deepLinkParams?.numRooms ? deepLinkParams?.numRooms : 0,
        },
      },
      search_date_type: isFlexibleDateSearch ? 'Flexible Dates' : 'With Dates',
      search_dates_flexible: isFlexibleDateSearch,
    };
    updatedSessionData = {
      ...updatedSessionData,
      cacheData: {
        ...updatedSessionData?.cacheData,
        data: {
          ...updatedSessionData?.cacheData?.data,
          AriesSearch: ariesSearch,
        },
      },
    };
    await updateSessionObjfunc(ariesSearch);
  };
  if (!skipDestinationCall) {
    await getDestinationDetails();
  } else {
    await updateSessionData({});
  }
  return {
    operationSignatures: OfferSignatures,
    featureFlagAemltsHost: featureFlagAemltsHost ?? null,
    aemHost: aemHost ?? null,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
    sessionData: updatedSessionData,
    featureFlags: { featureFlagLogo },
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(offersSpecificData);
export default pageRenderer;
